<template>
  <div class="page-social-sentiments extra-large-page">
    <v-card
      max-width="600"
      class="mx-auto accent mt-sm-8 mt-md-12 mt-lg-16"
      flat
    >
      <div class="d-flex flex-wrap align-center justify-space-between pr-md-4">
        <div>
          <v-card-title>
            Social Sentiments
          </v-card-title>

          <v-card-subtitle class="grey--text text--darken-2">
        Add upto {{ maxItems }} links at once.
          </v-card-subtitle>
        </div>

        <v-btn
          :to="{ name: 'SocialSentimentsHistory' }"
          class="shadow--primary ml-4 ml-md-0"
          color="primary"
          depressed
          large
        >
          History
        </v-btn>
      </div>

      <v-card-text>
        <!-- Show the error messages here -->
        <p
          v-if="items.length > maxItems"
          class="red--text"
        >
          Please enter a maximum of {{ maxItems }} links at once
        </p>

        <!-- Show error messages for all invalid profile/links -->
        <p
          v-for="(item, index) in invalidItems"
          :key="index"
          class="red--text"
        >
          Link #{{ items.indexOf(item) + 1 }} ({{ item }}) is not valid
        </p>

        <!-- Show the select inputs -->
        <div class="d-flex mt-3 mt-sm-0">
          <!-- show the input for searching terms -->
          <div class="search-input">
            <v-select
              solo
              flat
              hide-details
              v-model="selectedType"
              :items="typeOptions"
              placeholder="Link Type"
            ></v-select>
          </div>
          <!-- the select options for different platforms -->
          <platform-selector
            :value="selectedPlatform"
            class="platform-selector"
            @input="handlePlatformChange"
          />
        </div>

        <!-- Show the textarea input -->
        <v-textarea
          solo
          flat
          hide-details
          class="mt-4"
          v-model="itemsInput"
          placeholder="Profile/Post URLs"
        ></v-textarea>
      </v-card-text>

      <v-card-actions class="px-4">
        <v-spacer></v-spacer>

        <v-btn
          :class="{ 'shadow--primary': !shouldCTABeDisabled }"
          :disabled="shouldCTABeDisabled"
          :loading="isLoading"
          color="primary"
          type="submit"
          large
          depressed
          @click="handleSubmit"
        >
          Analyze
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- Show the progress value for each analysis -->
    <v-dialog
      v-model="shouldShowDialog"
      max-width="600"
      scrollable
    >
      <v-card>
        <v-card-title>
          Social Sentiments Analysis
        </v-card-title>

        <v-card-text class="black--text">
          <!-- Show the progress items here -->
          <div
            v-for="(item, index) in progressItems"
            :key="item.symbol"
            class="mb-3"
          >
            <!-- If we don't have the model data yet, show a loader -->
            <template v-if="item.model === null">
              <div class="text-center mb-6">
                <v-progress-circular
                  color="primary"
                  indeterminate
                />
              </div>
            </template>
            <!-- Otherwise show the real data -->
            <template v-else>
              <!-- Show platform and profile type in one row -->
              <div class="d-flex justify-space-between">
                <div class="d-flex">
                  <v-img height="20" width="20" max-width="20" :src="platformIconMap[item.model.platform]" />

                  <span class="pl-3">
                    {{ capitalizeString(item.model.platform) }}
                  </span>
                </div>

                <span>
                  {{ capitalizeString(item.model.for) }}
                </span>
              </div>

              <!-- Show the query, status and score here -->
              <div class="mt-1 py-3 d-flex justify-space-between align-center">
                <!-- show the model query -->
                <query-chip :model="item.model" />

                <!-- Show status and score here -->
                <div class="d-flex">
                  <score-chip :score="item.model.score" />
                  <status-chip :status="item.model.status" class="ml-3" />
                </div>
              </div>

              <!-- If possible show the distribution chart here -->
              <distribution-chart
                v-if="item.model.score"
                :data="item.model.distribution"
              />
            </template>

            <!-- Show a divide at the end -->
            <v-divider v-if="index < (progressItems.length - 1)" />
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>

          <v-btn
            text
            color="primary"
            @click="shouldShowDialog = false"
          >
            Okay
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
// Helper functions
import platformRegex from "@/helpers/platformRegex"
import isURL from "@/helpers/isURL"

// Selector Blocks
const PlatformSelector = () => import("@/blocks/common/selectors/PlatformSelector.vue")

// Import children components
import QueryChip from '@/components/social-sentiments/QueryChip.vue'
import ScoreChip from '@/components/social-sentiments/ScoreChip.vue'
import StatusChip from '@/components/social-sentiments/StatusChip.vue'
import DistributionChart from '@/components/social-sentiments/DistributionChart.vue'

export default {
  name: "SocialSentimentsForm",

  components: {
    PlatformSelector,

    QueryChip,
    ScoreChip,
    StatusChip,
    DistributionChart
  },

  data: () => ({
    // The string to be used to show the progress in a popup
    deviceId: null,
    shouldShowDialog: false,
    progressItems: [],

    selectedType: null,
    selectedPlatform: "instagram",

    // Store the entire links input in this string
    itemsInput: "",

    minItems: 1,
    maxItems: 5,

    isLoading: false
  }),

  computed: {
    /**
     * Compute the input string and try to generate an array
     *
     * @returns {Array}
     */
    items() {
      const breakCharacter = "*STRING&ENDS&HERE*"

      return this.itemsInput.replaceAll(" ", breakCharacter)
        .replaceAll("\n", breakCharacter)
        .split(breakCharacter)
        .map((item) => item.trim())
        .filter((item) => Boolean(item))
    },

    /**
     * Whether or not the items are all within range
     *
     * @returns {Boolean}
     */
    areItemsOverflowing() {
      return this.items.length >= this.maxItems
    },

    /**
     * Make sure that all the items are valid string values, otherwise return an array of line numbers which are invalid
     *
     * @returns {Array}
     */
    invalidItems() {
      return this.items.filter((item) => {
        // Check if the input is kind of an URL
        if (isURL(item)) {
          // If the type selected is "post", it only needs to be a valid URL
          if (this.selectedType === "post") {
            return false
          }

          // Otherwise, try getting the regex match for it
          const match = item.match(platformRegex[this.selectedPlatform])

          // If the match is found, hence found the userID for this link
          if (match !== null && match[1]) {
            return false
          }
          else {
            return true
          }
        }
        // Fallback to true
        else {
          return false
        }
      })
    },

    /**
     * Whether or not the submit button be disabled
     *
     * @returns {Boolean}
     */
    shouldCTABeDisabled() {
      return (this.items.length < this.minItems) || this.isLoading || this.invalidItems.length > 0
    },

    /**
     * Select input possible option values for type
     *
     * @returns {Array}
     */
    typeOptions() {
      const items = [
        {
          text: "Profile",
          value: "profile",
        },

        {
          text: "Post",
          value: "post",
        }
      ]

      return items.filter((item) => {
        if (this.selectedPlatform === "youtube" && item.value === "profile") {
          return false
        }
        else {
          return true
        }
      })
    }
  },

  // Options API watcher functions
  watch: {
    typeOptions(items) {
      // If we only have one type option, select it as default
      if (items.length === 1) {
        this.selectedType = items[0].value
      }
    }
  },

  // Options API methods object
  methods: {
    /**
     * When the platform selector's value has been changed
     *
     * @param {String} value
     * @returns {void}
     */
    handlePlatformChange(value) {
      this.selectedPlatform = value
      this.selectedType = null
    },

    /**
     * When the submit button is clicked
     *
     * @returns {void}
     */
    async handleSubmit() {
      // If some request is in queue
      if (this.isLoading) {
        this.$store.dispatch("toasts/add", {
          text: "Please wait ..."
        })

        return false
      }

      // Check if the there are at least 2 items
      else if (this.items.length < this.minItems) {
        this.$store.dispatch("toasts/add", {
          text: `Please add at least ${this.minItems} links`
        })

        return false
      }

      else if (this.selectedType === null) {
        this.$store.dispatch("toasts/add", {
          text: "Please select an export file type"
        })

        return false
      }

      // Otherwise, make the request
      else {
        // Put a loader
        this.isLoading = true
        this.progressItems = []

        // For each link, make a network request
        for (const query of this.items) {
          try {
            // Also a trackerId to update the status here
            const trackerId = String(Date.now() + Math.floor(Math.random() * 100))

            await axios({
              url: "/api/social-sentiments",
              method: "POST",
              data: {
                platform: this.selectedPlatform,
                deviceId: this.deviceId,
                for: this.selectedType,
                trackerId,
                query
              }
            })

            // Also add the successful ones to the progress items array
            this.progressItems.push({
              query,
              trackerId,
              model: null,
              symbol: Symbol(),
            })
          } catch (error) {
            this.$store.dispatch("toasts/add", {
              text: error?.response?.data?.error?.message || "An error occurred!"
            })
          }
        }

        // If we have multiple progress items
        if (this.progressItems.length > 0) {
          // Start showing a loader now
          this.shouldShowDialog = true

          // Reset the form input value
          this.itemsInput = ""
        }

        // Hide the loader
        this.isLoading = false
      }
    },

    /**
     * Whenever a status changes, we can either replace it or refetch all items
     *
     * @param {Event} event
     * @returns {void}
     */
    handleStatusChangeEvent(event) {
      // Make sure that the deviceId matches
      if (event.detail.data.deviceId !== this.deviceId) return

      // Make sure that we do have the trackerId
      if (!event?.detail?.data?.trackerId) return

      // Get the data from the event
      const model = event?.detail?.data?.model

      // If model not found, then don't continue
      if (!model) return

      // Check if we have this model in our local state
      const index = this.progressItems.findIndex((search) => search.trackerId === event.detail.data.trackerId)

      // If we do have this indeed
      if (index !== -1) {
        // Update the value for progress model data
        this.progressItems[index].model = model

        // Force the table to re-render
        this.reloadKey = Symbol()
      }
    }
  },

  // When the Options API mounted lifecycle hook is called
  mounted() {
    // Compute the deviceId to be shown the progress of
    this.deviceId = String(Date.now())

    // Listen to update events
    window.addEventListener("SocialSentimentStatusChange", this.handleStatusChangeEvent)
  },

  // Unsubscribe from these events
  beforeDestroy() {
    window.removeEventListener("SocialSentimentStatusChange", this.handleStatusChangeEvent)
  }
}
</script>

<style lang="stylus">
// to contain the entire box structure in large viewport
.page-social-sentiments
  overflow-x auto

  .options-container
    max-width 1120px
    margin 0 auto

  .results-container
    max-width 1320px
    margin 0 auto

  .search-input
    width 100%

    .v-input__slot
      height 48px
      border-top-right-radius 0
      border-bottom-right-radius 0

    .v-label
      padding-top 4px

  .platform-selector
    .v-input__slot
      border-top-left-radius 0
      border-bottom-left-radius 0
</style>
